<script setup lang="ts">
import { useImage } from '@vueuse/core';
import { computed, ref, watch } from 'vue';

import { trackImageLoad } from '@libs/feature-toggle';

import SemboNoImageIllustration from '../../art/SemboNoImageIllustration.vue';

import type { AppImgType } from './AppImg.model';

interface AppImgProps {
  src: string;
  imgType?: AppImgType;
  alt?: string;
  trackTime?: boolean;
}

interface AppImgEmit {
  (eventName: 'img-load-error', src: string): void;
  (eventName: 'click'): void;
}

const props = withDefaults(defineProps<AppImgProps>(), {
  imgType: 'cover',
  trackTime: false,
});
const emit = defineEmits<AppImgEmit>();
const startTime = ref(Date.now());

watch(
  () => props.src,
  () => {
    startTime.value = Date.now();
  }
);

const imageOptions = computed(() => ({ src: props.src }));

const { isLoading, error } = useImage(imageOptions, {
  onError: () => {
    if (props.trackTime) {
      trackImageLoad({
        loadTime: Date.now() - startTime.value,
        error: true,
      });
    }
    emit('img-load-error', props.src);
  },
});

if (props.trackTime) {
  watch(isLoading, () => {
    if (!isLoading.value) {
      trackImageLoad({
        loadTime: Date.now() - startTime.value,
      });
    }
  });
}
</script>

<template>
  <img
    v-if="!isLoading && !error"
    :src="props.src"
    :alt="props.alt"
    :class="{
      img: true,
      [props.imgType]: props.imgType,
    }"
    @click="emit('click')"
  />
  <div
    v-else
    :class="{
      img: true,
      [props.imgType]: props.imgType,
      noImage: !!error,
    }"
  >
    <SemboNoImageIllustration v-if="!!error" />
  </div>
</template>

<style scoped lang="scss">
.img {
  &.cover {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }

  &.contain {
    height: 100%;
    width: 100%;
    object-fit: contain;
  }

  &.scaleX {
    height: 100%;
    width: auto;
  }

  &.noImage {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(--color-neutral-0);
  }
}
</style>
